import { useEffect } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { getOptionsFromRefList } from "@/util/common_function";
import { useSelector } from "react-redux";

const ReportInformation = ({ report = {}, startingPoint = "" }) => {
  const [form] = Form.useForm();
  const { bookingsFieldsEnabled } = useSelector((s) => s?.bookings);

  // -----------------------------------------------------------------------
  // Sync report prop → form fields
  // -----------------------------------------------------------------------
  useEffect(() => {
    const reportDateTime = report.dateReported && report.timeReported
      ? dayjs(`${report.dateReported} ${report.timeReported}`, "MM/DD/YYYY HH:mm")
      : null;

    const dateTimeOccurred = report.dateOccurred && report.timeOccurred
      ? dayjs(`${report.dateOccurred} ${report.timeOccurred}`, "MM/DD/YYYY HH:mm")
      : null;

    form.setFields([
      { name: "reportedBy",      value: report.reportedBy  || "" },
      { name: "reportDateTime",  value: reportDateTime          },
      { name: "enteredBy",       value: report.enteredBy   || "" },
      { name: "bookingNumber",   value: report.bookingID   || "" },
      { name: "caseNumber",      value: report.caseNumber  || "" },
      { name: "dateTimeOccurred", value: dateTimeOccurred       },
      { name: "shift",           value: report.shift       || "" },
      { name: "reportType",      value: report.reportType  || "" },
    ]);
  }, [report, form]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <FieldSecurityProvider
      entity="ReportInformation"
      startingPoint={startingPoint}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <Form form={form} layout="vertical">
        <StyledGridRow gutter={16} justify="start">

          {/* Reporting Officer */}
          <StyledGridCol lg={12}>
            <SecureFormItem
              fieldName="reportedBy"
              formItemProps={{ name: "reportedBy", label: "Reporting Officer" }}
            >
              <Select
                options={getOptionsFromRefList("", true, false, true)}
                placeholder="Select Officer"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Report Date/Time */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="reportDateTime"
              formItemProps={{ name: "reportDateTime", label: "Report Date/Time" }}
            >
              <CommonDatePicker />
            </SecureFormItem>
          </StyledGridCol>

          {/* Entered By */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="enteredBy"
              formItemProps={{ name: "enteredBy", label: "Entered By" }}
            >
              <Select
                options={getOptionsFromRefList("", true, false, true)}
                placeholder="Select Entered By"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Booking # — display only, not a form input */}
          <StyledGridCol>
            <SecureFormItem
              fieldName="bookingNumber"
              overrideDisabled
              formItemProps={{ name: "bookingNumber", label: "Booking #" }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          {/* Case Number */}
          <StyledGridCol lg={4}>
            <SecureFormItem
              fieldName="caseNumber"
              formItemProps={{ name: "caseNumber", label: "Case Number" }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          {/* Date/Time Occurred */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="dateTimeOccurred"
              formItemProps={{ name: "dateTimeOccurred", label: "Date/Time Occurred" }}
            >
              <CommonDatePicker />
            </SecureFormItem>
          </StyledGridCol>

          {/* Shift */}
          <StyledGridCol lg={5}>
            <SecureFormItem
              fieldName="shift"
              formItemProps={{ name: "shift", label: "Shift" }}
            >
              <Select
                options={getOptionsFromRefList("Shift", false, false, true)}
                placeholder="Select Shift"
              />
            </SecureFormItem>
          </StyledGridCol>

          {/* Type of Report */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="reportType"
              formItemProps={{ name: "reportType", label: "Type of Report" }}
            >
              <Select
                options={getOptionsFromRefList("Charges_reportTypes", false, false, true)}
                placeholder="Select Report Type"
              />
            </SecureFormItem>
          </StyledGridCol>

        </StyledGridRow>
      </Form>
    </FieldSecurityProvider>
  );
};

export default ReportInformation;